import moment from 'moment'
import _ from 'lodash'

import InvoicesRepository from './InvoicesRepository'
import Utils from './Utils'

export default class DbInvoiceEditor {

  initialize = props => {
    if (props.invoice && props.teamId
      && (props.teamId !== this.teamId || props.invoice !== this.invoice)) {
      this.teamId = props.teamId
      this.invoice = props.invoice
      this.invoiceItems = null
      this.invoicePhotos = null
      this.customerInfo = null
      this.referencedEvents = null
      this.invoiceListener = InvoicesRepository.addInvoiceListener(
        this.teamId, this.invoice.id, this.onInvoiceValue)
      this.invoiceItemsListener = InvoicesRepository.addInvoiceItemsListener(
        this.teamId, this.invoice.id, this.onInvoiceItemsValue)
      this.invoicePhotosListener = InvoicesRepository.addInvoicePhotosListener(
        this.teamId, this.invoice.id, this.onInvoicePhotosValue)
      this.customerListener = InvoicesRepository.addCustomerListener(
        this.teamId, this.invoice.customerId, this.onCustomerValue)
      this.referencedEventsListener = InvoicesRepository.addInvoiceReferencedEventsListener(
        this.teamId, this.invoice.id, this.onInvoiceReferencedEventsValue)
    }
  }

  getTeamId = () => this.teamId

  getBillingEntity = () => this.invoice
  
  release = () => {
    if (this.invoiceListener) {
      InvoicesRepository.removeInvoiceListener(
        this.teamId, this.invoice.id, this.onInvoiceValue)
      this.invoiceListener = null
    }
    if (this.invoiceItemsListener) {
      InvoicesRepository.removeInvoiceItemsListener(
        this.teamId, this.invoice.id, this.onInvoiceItemsValue)
      this.invoiceItemsListener = null
    }
    if (this.invoicePhotosListener) {
      InvoicesRepository.removeInvoicePhotosListener(
        this.teamId, this.invoice.id, this.onInvoicePhotosValue)
      this.invoicePhotosListener = null
    }
    if (this.customerListener) {
      InvoicesRepository.removeCustomerListener(
        this.teamId, this.invoice.customerId, this.onCustomerValue)
      this.customerListener = null
    }
    if (this.referencedEventsListener) {
      InvoicesRepository.removeInvoiceReferencedEventsListener(
        this.teamId, this.invoice.customerId, this.onInvoiceReferencedEventsValue)
      this.referencedEventsListener = null
    }
  }
  
  notifyValueUpdatedListener = () => {
    if (this.valueUpdatedListener) {
      this.valueUpdatedListener()
    }
  }

  addValueUpdatedListener = listener => {
    this.valueUpdatedListener = listener
  }

  onCustomerValue = snapshot => {
    this.customerInfo = snapshot && snapshot.val()
    this.notifyValueUpdatedListener()
  }

  onInvoiceReferencedEventsValue = snapshot => {
    const referencedEvents = snapshot && snapshot.val()
    this.referencedEvents = referencedEvents && _.map(referencedEvents) || []
    this.notifyValueUpdatedListener()
  }

  onInvoiceValue = snapshot => {
    this.invoice = snapshot && snapshot.val() || {}
    this.notifyValueUpdatedListener()
  }

  onInvoiceItemsValue = snapshot => {
    const items = snapshot && snapshot.val()
    this.invoiceItems = items && _.map(items) || []
    this.notifyValueUpdatedListener()
  }

  onInvoicePhotosValue = snapshot => {
    const photos = snapshot && snapshot.val()
    this.invoicePhotos = photos && _.map(photos) || []
    this.notifyValueUpdatedListener()
  }

  getCustomerId = () => this.invoice.customerId
  
  getContactId = () => this.invoice.contactId

  setCustomerInfo = customerInfo => {
    if (this.invoice.customerId !== customerInfo.customerId || this.invoice.contactId !== customerInfo.contactId) {
      if (this.invoice.customerId !== customerInfo.customerId) {
        InvoicesRepository.removeCustomerListener(this.teamId, this.invoice.customerId, this.onCustomerValue)
        InvoicesRepository.addCustomerListener(this.teamId, customerInfo.customerId, this.onCustomerValue)
      }
      InvoicesRepository.updateInvoiceCustomerInfo(this.teamId, this.invoice, customerInfo)
    }
  }

  getContactInfo = () => {
    return this.invoice.contactId
      && this.customerInfo
      && this.customerInfo.contactsList
      && this.customerInfo.contactsList[this.invoice.contactId]
  }

  getCustomerInfo = () => {
    // Return a normalized model with customer and contact properties
    return {
      customerId: this.invoice.customerId,
      contactId: this.invoice.contactId,
      customer: {
        id: this.invoice.customerId,
        name: this.customerInfo && this.customerInfo.name
      },
      contact: this.getContactInfo()
    }
  }

  isLoadingItems = () => this.invoiceItems === null

  getItems = () => this.invoiceItems

  addItem = item => {
    InvoicesRepository.addInvoiceItem(this.teamId, this.invoice, item)
    InvoicesRepository.updateInvoiceTotal(this.teamId, this.invoice,
      this.invoice.total + Utils.getItemAmount(item))
  }

  deleteItem = item => {
    InvoicesRepository.deleteInvoiceItem(this.teamId, this.invoice, item)
    InvoicesRepository.updateInvoiceTotal(this.teamId, this.invoice,
      this.invoice.total - Utils.getItemAmount(item))
  }

  updateItemName = (item, name) => {
    item.name = name
    InvoicesRepository.updateItem(this.teamId, this.invoice, item)
  }

  updateItemDescription = (item, description) => {
    item.description = description
    InvoicesRepository.updateItem(this.teamId, this.invoice, item)
  }

  updateItemRate = (item, rate) => {
    const oldAmount = Utils.getItemAmount(item)
    item.rate = rate
    const newAmount = Utils.getItemAmount(item)
    InvoicesRepository.updateItem(this.teamId, this.invoice, item)
    InvoicesRepository.updateInvoiceTotal(this.teamId, this.invoice,
      this.invoice.total + newAmount - oldAmount)
  }

  updateItemTaxPercent = (item, taxPercent) => {
    const oldAmount = Utils.getItemAmount(item)
    item.taxPercent = taxPercent
    const newAmount = Utils.getItemAmount(item)
    InvoicesRepository.updateItem(this.teamId, this.invoice, item)
    InvoicesRepository.updateInvoiceTotal(this.teamId, this.invoice,
      this.invoice.total + newAmount - oldAmount)
  }

  updateItemQuantity = (item, quantity) => {
    const oldAmount = Utils.getItemAmount(item)
    item.quantity = quantity
    const newAmount = Utils.getItemAmount(item)
    InvoicesRepository.updateItem(this.teamId, this.invoice, item)
    InvoicesRepository.updateInvoiceTotal(this.teamId, this.invoice,
      this.invoice.total + newAmount - oldAmount)
  }

  getTotal = () => this.invoice.total

  getTitle = () => {
    return this.invoice.title
  }

  setTitle = title => {
    InvoicesRepository.setInvoiceTitle(this.teamId, this.invoice, title)
  }

  getNumber = () => {
    return this.invoice.number
  }

  setNumber = number => {
    InvoicesRepository.setInvoiceNumber(this.teamId, this.invoice, number)
  }

  getNotes = () => {
    return this.invoice.notes
  }

  setNotes = notes => {
    InvoicesRepository.setInvoiceNotes(this.teamId, this.invoice, notes)
  }

  getPaymentDueDate = () => {
    const result = moment(this.invoice.paymentDueDate)
    return result.isValid() ? result : null
  }

  setPaymentDueDate = date => {
    InvoicesRepository.setInvoicePaymentDueDate(this.teamId, this.invoice, date.toISOString())
  }

  getBillDate = () => {
    const result = moment(this.invoice.invoiceDate)
    return result.isValid() ? result : null
  }
  
  setBillDate = date => {
    InvoicesRepository.setInvoiceDate(this.teamId, this.invoice, date.toISOString())
  }

  isLoadingReferencedEvents = () => this.referencedEvents === null

  getReferencedEvents = () => this.referencedEvents

  addReferencedEvent = eventInfo => {
    InvoicesRepository.addInvoiceReferencedEvent(this.teamId, this.invoice, eventInfo)
  }

  removeReferencedEvent = eventInfo => {
    InvoicesRepository.removeInvoiceReferencedEvent(this.teamId, this.invoice, eventInfo)
  }

  isLoadingPhotos = () => this.invoicePhotos === null

  getPhotos = () => this.invoicePhotos

  addPhotos = photos => {
    if (this.invoicePhotos) {
      // Make sure we don't add the same photo twice.
      const unique = []
      for (const photo of photos) {
        const found = this.invoicePhotos.find(p => p.photoId === photo.photoId)
        if (!found) {
          unique.push(photo)
        }
      }
      InvoicesRepository.addInvoicePhotos(this.teamId, this.invoice, unique)
    }
  }

  /** Realtime updates, no need for save */
  save = () => {
    return { success: true, invoice: this.invoice }
  }

  delete = () => {
    InvoicesRepository.deleteInvoice(this.teamId, this.invoice)
  }

}



// WEBPACK FOOTER //
// ./src/billing/infra/DbInvoiceEditor.js